import { Logo } from '@common/components/brand/Logo';
import { LanguageMenu } from '@common/components/layout/LanguageMenu';
import { ThemeMenu } from '@common/components/layout/ThemeMenu';
import { useNomePublico } from '@features/campanhas';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks';
import { consumeRedirect } from '../utils/redirectAfterLogin';
import { LoginForm } from './LoginForm';

/** `?from=` já vem decodificado pelo `URLSearchParams`; pode trazer `?passo=` depois do id. */
const CAMPANHA_DO_LINK = /^\/responder\/([0-9a-f-]{36})/;

function safeRedirect(path: string | null): string {
  if (!path?.startsWith('/') || path.startsWith('//') || path === '/login') return '/';
  return path;
}

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { isAuthenticated, isLoading } = useAuth();
  const [params] = useSearchParams();
  // Spec 33 R20: veio do link da campanha, mostra o nome dela; carregando, com erro ou 404, sem título.
  const campanhaId = params.get('from')?.match(CAMPANHA_DO_LINK)?.[1] ?? null;
  const nomeCampanha = useNomePublico(isAuthenticated ? null : campanhaId).data;

  useEffect(() => {
    const original = document.title;
    document.title = `${t('auth:loginTitle')} · ${t('common:appName')}`;
    return () => {
      document.title = original;
    };
  }, [t]);

  if (!isLoading && isAuthenticated) {
    const target = safeRedirect(params.get('from') ?? consumeRedirect());
    return <Navigate to={target} replace />;
  }

  return (
    <main className="grid min-h-dvh md:grid-cols-2">
      <aside
        className="relative hidden flex-col justify-between overflow-hidden p-12 text-brand-primary-foreground md:flex"
        style={{
          background:
            'linear-gradient(135deg, var(--color-brand-primary) 0%, var(--color-brand-primary-active) 100%)',
        }}
      >
        <div aria-hidden className="pointer-events-none absolute -right-32 -bottom-40 opacity-15">
          <Logo iconOnly tone="inverted" className="size-[520px]" />
        </div>

        <div className="relative z-10">
          <Logo size="lg" tone="inverted" />
        </div>

        <div className="relative z-10 flex max-w-md flex-col gap-3">
          <h2 className="text-balance text-4xl font-semibold leading-tight tracking-tight">
            {t('common:appName')}
          </h2>
          <p className="text-pretty text-lg opacity-90">{t('auth:tagline')}</p>
        </div>

        <div className="relative z-10 text-xs opacity-70">
          © {new Date().getFullYear()} NTConsult
        </div>
      </aside>

      <section className="flex flex-col bg-surface">
        <header className="flex items-center justify-end gap-1 p-4">
          <LanguageMenu />
          <ThemeMenu />
        </header>

        <div className="flex flex-1 items-center justify-center px-6 pb-6">
          <div className="flex w-full max-w-sm flex-col gap-7">
            <div className="flex flex-col items-center gap-3 md:hidden">
              <Logo size="lg" className="h-auto max-w-full" />
            </div>

            <div className="flex flex-col gap-1.5">
              {nomeCampanha && (
                <p className="text-balance text-xl font-semibold text-brand-primary">
                  {nomeCampanha}
                </p>
              )}
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {t('auth:loginTitle')}
              </h1>
              <p className="text-sm text-foreground-muted">{t('auth:loginSubtitle')}</p>
            </div>

            <LoginForm />
          </div>
        </div>

        <footer className="border-t border-border px-6 py-3 text-center text-xs text-foreground-muted md:text-right">
          v{__APP_VERSION__}
        </footer>
      </section>
    </main>
  );
}
